import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { routeDelaysManager } from "@/setup/routes/delay.routes";
import { DelayAreaSelector } from "@/shared/selectors/DelayAreaSelector";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
	useUserContext,
} from "@dexteel/mesf-core";
import {
	Checkbox,
	Divider,
	FormControlLabel,
	Grid2,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DelaysManagerTable } from "./components/table/DelaysManagerTable";
import { DelayByShift } from "./models/DelayByShift";
import {
	getDelay,
	getDelaysByShift,
} from "./repositories/DelaysManagerRepository";

type useDelaysByShiftProps = {
	shiftId: number | undefined;
	delayAreaAssetId?: number | null;
	includeMicroDelays?: boolean;
	onlyUndeclared?: boolean;
	onError: (error: Error) => void;
};

export const useDelaysByShift = ({
	shiftId,
	delayAreaAssetId,
	includeMicroDelays = false,
	onlyUndeclared = false,
	onError,
}: useDelaysByShiftProps) => {
	return useQuery<DelayByShift[], Error>({
		queryKey: [
			"delays",
			shiftId,
			delayAreaAssetId,
			includeMicroDelays,
			onlyUndeclared,
		],
		queryFn: ({ signal }) =>
			getDelaysByShift(
				{
					shiftId: shiftId || null,
					delayAreaAssetId: delayAreaAssetId,
					includeMicroDelays: includeMicroDelays,
					onlyUndeclared: onlyUndeclared,
				},
				signal,
			),
		enabled: !!shiftId,
		onError: onError,
	});
};

type useDelayByIdProps = {
	delayId: number | null;
	onError: (error: Error) => void;
};

export const useDelayById = ({ delayId, onError }: useDelayByIdProps) => {
	return useQuery<DelayByShift[], Error>({
		queryKey: ["delays", delayId],
		queryFn: ({ signal }) =>
			getDelay(
				{
					delayId,
				},
				signal,
			),
		onError: onError,
	});
};

type Props = {};

export const DelaysManagerPage = (props: Props) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [selectedAssetId, setSelectedAssetId] = useState<number | null>(
		Number(defaultAreaId) || null,
	);

	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();
	const [params] = useSearchParams();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const delayId = params.get("delayId");
	const action = params.get("action") as "edit" | "justify" | "split" | null;
	const includeMicroDelays = params.get("includeMicroDelays") === "true";
	const onlyUndeclared = params.get("onlyUndeclared") === "true";

	const handleAssetChange = (newAssetId: number | null) => {
		setSelectedAssetId(newAssetId);
	};

	const updateUrl = (
		newIncludeMicroDelays?: boolean,
		newOnlyUndeclared?: boolean,
		newAction?: string | null,
		newDelayId?: string | null,
	) => {
		const shiftId = get(shiftInfo, "CurrentShiftId", null);
		const urlParams = new URLSearchParams();
		const targetDelayId = newDelayId !== undefined ? newDelayId : delayId;
		if (targetDelayId) urlParams.append("delayId", targetDelayId);
		const targetAction = newAction !== undefined ? newAction : action;
		if (targetAction) urlParams.append("action", targetAction);
		if (newIncludeMicroDelays ?? includeMicroDelays)
			urlParams.append("includeMicroDelays", "true");
		if (newOnlyUndeclared ?? onlyUndeclared)
			urlParams.append("onlyUndeclared", "true");

		navigate(`${routeDelaysManager}/${shiftId}?${urlParams.toString()}`);
	};

	const {
		data: delays,
		refetch,
		isLoading,
	} = useDelaysByShift({
		shiftId: shiftInfo?.CurrentShiftId,
		delayAreaAssetId: selectedAssetId,
		includeMicroDelays: includeMicroDelays,
		onlyUndeclared: onlyUndeclared,
		onError: (e) => setError(e.message),
	});

	useEffect(() => {
		if (defaultAreaId && selectedAssetId === null) {
			setSelectedAssetId(Number(defaultAreaId) || null);
		}
	}, [defaultAreaId]);

	useEffect(() => {
		if (shiftInfo) {
			updateUrl();
			(async () => {
				await refetch();
			})();
		}
	}, [shiftInfo, selectedAssetId, includeMicroDelays, onlyUndeclared]);

	// When navigating with a delayId, look up the delay's shift and asset
	// to load the correct shift context (handles cross-mill navigation).
	const [delayLookupDone, setDelayLookupDone] = useState(false);

	useEffect(() => {
		if (delayId && !delayLookupDone) {
			(async () => {
				try {
					const delays = await getDelay({ delayId: Number(delayId) });
					if (delays && delays.length > 0) {
						const delay = delays[0];
						if (delay.AssetId) setSelectedAssetId(delay.AssetId);
						await getShiftDataFromAPI(
							null,
							delay.ShiftId,
							delay.AssetId ?? selectedAssetId!,
						);
					}
				} catch {
					// Fallback: load current shift
					await getShiftDataFromAPI(
						null,
						0,
						selectedAssetId == 10 ? Number(defaultAreaId) : selectedAssetId!,
					);
				}
				setDelayLookupDone(true);
			})();
		} else if (!delayId) {
			if (shiftIdParam) {
				(async () => {
					const currentDate = shiftInfo?.CurrentProductionDate ?? null;
					await getShiftDataFromAPI(
						currentDate,
						0,
						selectedAssetId == 10 ? Number(defaultAreaId) : selectedAssetId!,
					);
				})();
			} else {
				(async () => {
					await getShiftDataFromAPI(
						null,
						0,
						selectedAssetId == 10 ? Number(defaultAreaId) : selectedAssetId!,
					);
				})();
			}
		}
	}, [selectedAssetId, defaultAreaId]);

	return (
		<>
			<HelmetDexteel title="Delays Manager" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{ backgroundColor: pageBgColor, minHeight: "calc(100vh - 160px)" }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Delays Manager
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Grid2
						container
						size={{ md: 12 }}
						justifyContent="flex-start"
						alignItems="center"
						spacing={2}
					>
						<Grid2 maxWidth={"fit-content"}>
							<ShiftDayNavigatorControl
								preventInit
								assetId={
									selectedAssetId == 10
										? Number(defaultAreaId)
										: selectedAssetId!
								}
							/>
						</Grid2>
						<Grid2 size={{ md: 1 }}>
							<DelayAreaSelector
								value={selectedAssetId}
								onChange={handleAssetChange}
								isFiltering
							/>
						</Grid2>
						<Grid2 size={{ xs: 4 }}>
							<FormControlLabel
								control={
									<Checkbox
										checked={includeMicroDelays}
										color="primary"
										onChange={(event) => {
											updateUrl(event.target.checked, onlyUndeclared);
										}}
									/>
								}
								label="Include Microdelays"
							/>
						</Grid2>
					</Grid2>
				</Grid2>
				<Grid2 size={{ md: 12 }}>
					<Divider />
				</Grid2>
				<Grid2>
					<DelaysManagerTable
						delayId={delayId}
						action={action}
						isLoading={isLoading}
						rows={delays || []}
						refreshData={refetch}
						assetId={selectedAssetId!}
						updateUrl={updateUrl}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
